import React from 'react';
import ReactDOM from 'react-dom';
import ReactDOMClient from 'react-dom/client';

/**
 * React version compatibility utility for supporting React 16, 17, and 18.
 * Detects the React version at runtime and uses the appropriate rendering API.
 */

// Detect React version at runtime
export const REACT_VERSION = parseInt(React.version.split('.')[0], 10);
export const isReact18Plus = REACT_VERSION >= 18;

// Cache for React 18 roots to enable re-rendering
const rootCache = new Map();

/**
 * Hydrate app with React 16/17/18 support.
 * Uses hydrateRoot for React 18+ and legacy hydrate for React 16/17.
 *
 * @param {React.ReactElement} element - The React element to hydrate
 * @param {HTMLElement} container - The DOM container to hydrate into
 * @param {Function} callback - Optional callback executed after hydration
 * @returns {Root|void} - React 18 root object or void for legacy versions
 */
export function hydrateApp(element, container, callback) {
  if (isReact18Plus && ReactDOMClient && typeof ReactDOMClient.hydrateRoot === 'function') {
    // React 18+ uses hydrateRoot from 'react-dom/client'
    // Using dynamic require to avoid bundling issues with React 16/17
    const { hydrateRoot } = ReactDOMClient;
    const root = hydrateRoot(container, element, {
      onRecoverableError: error => {
        // Log hydration mismatches but don't crash
        console.error('Hydration error:', error);
      },
    });
    rootCache.set(container, root);
    // Callback executed after hydration (approximate behavior since React 18 removed the callback)
    if (callback) {
      // Use requestIdleCallback if available, otherwise setTimeout
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    }
    return root;
  } else {
    // React 16/17 uses legacy hydrate

    // eslint-disable-next-line react/no-deprecated
    return ReactDOM.hydrate(element, container, callback);
  }
}

/**
 * Render app with React 16/17/18 support (used in HMR).
 * Uses createRoot for React 18+ and legacy render for React 16/17.
 *
 * @param {React.ReactElement} element - The React element to render
 * @param {HTMLElement} container - The DOM container to render into
 * @returns {Root|void} - React 18 root object or void for legacy versions
 */
export function renderApp(element, container) {
  if (isReact18Plus && ReactDOMClient && typeof ReactDOMClient.createRoot === 'function') {
    // React 18+ uses createRoot from 'react-dom/client'
    const { createRoot } = ReactDOMClient;
    let root = rootCache.get(container);
    if (!root) {
      root = createRoot(container);
      rootCache.set(container, root);
    }
    root.render(element);
    return root;
  } else {
    // React 16/17 uses legacy render
    // eslint-disable-next-line react/no-render-return-value, react/no-deprecated
    return ReactDOM.render(element, container);
  }
}
